import React, { useImperativeHandle } from "react";
import { faLink, faPlayCircle } from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";

// Utility function to normalize timestamp (ensure it's available)
const normalizeTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const UrlCard = React.forwardRef(
  ({ card, topic, messages, w, h, displayVariant, displayParameters }, ref) => {
    const action = (setSelectedCard) => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    // Use safeMessages from messages prop
    const safeMessages = messages && messages.length > 0 ? messages : [];
    const effectiveLastMessage =
      safeMessages.length > 0 ? safeMessages[safeMessages.length - 1] : null;
    const content = effectiveLastMessage
      ? effectiveLastMessage.content
      : "No link available";
    const timestamp = effectiveLastMessage
      ? effectiveLastMessage.timestamp
      : null;

    const sizeKey = `${w}x${h}`;

    const renderContent = () => {
      switch (sizeKey) {
        case "1x1":
        case "1x2":
        case "2x1":
          return (
            <div className="flex items-center justify-center w-full h-full">
              <FontAwesomeIcon
                icon={faPlayCircle}
                className="text-indigo-400 text-4xl"
              />
            </div>
          );
        case "1x3":
        case "3x1":
          return (
            <div className="flex items-center justify-center w-full h-full">
              <FontAwesomeIcon
                icon={faPlayCircle}
                className="text-indigo-400 text-3xl mr-2"
              />
              <span className="text-sm font-semibold text-gray-700">
                {topic.name}
              </span>
            </div>
          );
        case "2x2":
        case "2x3":
        case "3x2":
        case "3x3":
          return (
            <div className="flex flex-col justify-between w-full h-full">
              <h2 className="text-md text-center font-semibold mb-2 text-gray-800">
                {topic.name}
              </h2>
              <button
                onClick={handleButtonClick}
                className="interactive-element w-full bg-indigo-400 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md"
              >
                <FontAwesomeIcon icon={faPlayCircle} className="mr-2" /> Open
                Link
              </button>
              <div className="flex items-center mt-2 text-indigo-400 truncate">
                <FontAwesomeIcon icon={faLink} className="mr-1" />
                <a
                  onClick={handleButtonClick}
                  onTouchEnd={handleButtonClick}
                  href={content}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs truncate hover:underline interactive-element"
                >
                  {content}
                </a>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {normalizeTimestamp(timestamp)}
              </p>
            </div>
          );
        default:
          return (
            <div className="flex flex-col justify-between w-full h-full">
              <h2 className="text-md font-semibold mb-2 text-gray-800">
                {topic.name}
              </h2>
              <button
                onClick={handleButtonClick}
                className="interactive-element w-full bg-indigo-400 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md"
              >
                <FontAwesomeIcon icon={faPlayCircle} className="mr-2" /> Open
                Link
              </button>
              <div className="flex items-center mt-2 text-indigo-400 truncate">
                <FontAwesomeIcon icon={faLink} className="mr-1" />
                <a
                  onClick={handleButtonClick}
                  onTouchEnd={handleButtonClick}
                  href={content}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs truncate hover:underline interactive-element"
                >
                  {content}
                </a>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {normalizeTimestamp(timestamp)}
              </p>
            </div>
          );
      }
    };

    const handleButtonClick = (e) => {
      window.open(content, "_blank", "noopener,noreferrer");
    };

    return (
      <div className="p-2 shadow-md cursor-pointer transition-transform duration-300 w-full h-full flex flex-col bg-gray-50">
        {renderContent()}
      </div>
    );
  }
);

UrlCard.variants = ["default", "preview", "detailed"];
UrlCard.variantParameters = {
  default: {},
  preview: {},
  detailed: {},
};
UrlCard.propTypes = {
  card: PropTypes.object.isRequired,
  topic: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
  displayVariant: PropTypes.string,
  displayParameters: PropTypes.object,
};

export default UrlCard;
